import React, { useState } from 'react';
import { useParams, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  User, Users, UserPlus, Mail, Trash2, Edit2, Check, AlertCircle, Copy, ChevronLeft, LogOut, ShieldCheck, Lock, X, KeyRound,
} from 'lucide-react';
import { useAuth, VIEWING_AS_NOTE } from '../context/AuthContext';
import Navbar from '../components/layout/Navbar';
import PageHeader from '../components/layout/PageHeader';
import GuidedTour from '../components/tour/GuidedTour';
import NotificationsToggle from '../components/NotificationsToggle';
import PrivacySettings from '../components/PrivacySettings';
import PasswordSection from '../components/PasswordSection';
import { WhatsAppButton } from '../components/ShareMenu';
import { Bell } from 'lucide-react';
import './MyAccountPage.css';

// B-76 QA: adding a child / asking to be linked are refused on a staff
// "view as" session (403 code 'impersonating') — say so in plain words.
const errorText = (err, fallback) => (err?.response?.data?.code === 'impersonating'
  ? VIEWING_AS_NOTE
  : (err?.response?.data?.error || fallback));

// WT-19: the address our account emails and messages give for help.
const ACCOUNT_HELP_EMAIL = 'hello@marakin.co.uk';

/**
 * MyAccountPage — `/:clubSlug/account`
 *
 * Sections:
 *   • Profile  — read-only display of name, email, role
 *   • My Family — CRUD on shadow-child users + invite co-parent
 *                 (hidden for platform-admin users — they aren't parents)
 *   • Sign out
 *
 * Family CRUD hits the v14 children.js routes under /api/clubs/:slug.
 * Day-2 of the parent-child workstream (see memory
 * project_marakin_parent_child_day2_built).
 */

// ─── Age / season helpers ───────────────────────────────────────
// RFU "season age" rule: a child is in U(X)s if they turn X between
// 1 Sep (season start) and 31 Aug (season end). Equivalent: their age
// on 31 Aug of the season's END year equals X.
// Season flips ~1 July for pre-season per Stuart's guidance.

function currentSeasonEndYear(now = new Date()) {
  const y = now.getFullYear();
  // m >= 6 → July or later → we're in the season ending NEXT August
  return now.getMonth() >= 6 ? y + 1 : y;
}

function seasonAgeOnAug31(dobISO, seasonEndYear = currentSeasonEndYear()) {
  if (!dobISO) return null;
  const d = new Date(dobISO);
  if (isNaN(d.getTime())) return null;
  const aug31 = new Date(seasonEndYear, 7, 31); // month index 7 = August
  let age = aug31.getFullYear() - d.getFullYear();
  const m = aug31.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && aug31.getDate() < d.getDate())) age--;
  return age;
}

/**
 * Parse "U10s", "U-10", "U6s Mini Rugby" etc. to {minAge, maxAge}.
 * Pilot rule: a team called Uxx matches exactly the season-age-X cohort.
 * Returns null for non-age-gated names (Senior, Veterans, Colts, etc.) —
 * these are treated as adult / not available to parents.
 */
function teamAgeBand(teamName) {
  if (!teamName) return null;
  const m = String(teamName).match(/\bU\s*-?\s*0?(\d{1,2})s?\b/i);
  if (!m) return null;
  const age = parseInt(m[1], 10);
  return { age, label: `U${age}s` };
}

function ageFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years--;
  return years;
}

function initialsOf(first, last) {
  const a = (first?.[0] || '').toUpperCase();
  const b = (last?.[0] || '').toUpperCase();
  return (a + b) || '?';
}

function formatDob(dob) {
  if (!dob) return '';
  return String(dob).slice(0, 10);
}

// ─── Page ────────────────────────────────────────────────────────
export default function MyAccountPage() {
  const { clubSlug } = useParams();
  const location = useLocation(); // UC-B5: return path for /login
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, getMembership, logout, loading } = useAuth();
  const membership = getMembership(clubSlug);
  // B-79: a sponsor login is the business's till account, not a member, so it
  // has no family here (the server refuses its children routes).
  const isSponsorLogin = membership?.role === 'sponsor';

  const [addingChild, setAddingChild] = useState(false);
  const [editingChildId, setEditingChildId] = useState(null);
  const [invitingChildId, setInvitingChildId] = useState(null);

  // Guided add-child (bulk invites, Aug 2026): the club can list a parent's
  // children on their invite; InviteAcceptPage stashes the names here. Each
  // suggestion pre-fills the normal add-child form — the parent supplies the
  // DOB — and the B-32 duplicate popup handles the second parent for free.
  const pendingKey = `marakin_pending_children:${clubSlug}`;
  const [pendingChildren, setPendingChildren] = useState(() => {
    try { return JSON.parse(localStorage.getItem(pendingKey) || '[]'); } catch { return []; }
  });
  const [childInitial, setChildInitial] = useState(null);
  const consumePending = (first, last) => {
    setPendingChildren(prev => {
      const next = prev.filter(c => !(c.firstName === first && (c.lastName || '') === (last || '')));
      try {
        if (next.length) localStorage.setItem(pendingKey, JSON.stringify(next));
        else localStorage.removeItem(pendingKey);
      } catch { /* storage unavailable — banner just persists this visit */ }
      return next;
    });
  };

  const { data: club } = useQuery({
    queryKey: ['club', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}`).then(r => r.data),
    enabled: !!clubSlug,
  });

  const { data: teams = [] } = useQuery({
    queryKey: ['teams', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/teams`).then(r => r.data),
    enabled: !!clubSlug,
  });

  const { data: childrenData, isLoading: childrenLoading } = useQuery({
    queryKey: ['my-children', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/my-children`).then(r => r.data),
    enabled: !!user && !!clubSlug && !isSponsorLogin,
  });
  const children = childrenData?.children || [];

  // B-32 / B-76: "already added by the other parent?" — ask to be linked.
  // The server answers the same whether or not a child of that name is
  // registered here (B-76: no probing for children), so this asks BEFORE a
  // child is added rather than after a duplicate is found.
  const [linkPrompt, setLinkPrompt] = useState(null); // { firstName, lastName, pending?: {firstName,lastName} }
  const [coparentMsg, setCoparentMsg] = useState('');
  // B-76 dedupe / B-90: the add-child reply's message (same words whether or
  // not another family already registered the child).
  const [addedMsg, setAddedMsg] = useState('');

  const createChild = useMutation({
    mutationFn: payload => axios.post(`/clubs/${clubSlug}/children`, payload).then(r => r.data),
    onSuccess: (data, vars) => {
      qc.invalidateQueries({ queryKey: ['my-children', clubSlug] });
      setAddingChild(false);
      setChildInitial(null);
      consumePending(vars?.firstName, vars?.lastName);
      setAddedMsg(data?.message || '');
    },
  });

  const requestCoparent = useMutation({
    mutationFn: ({ firstName, lastName }) =>
      axios.post(`/clubs/${clubSlug}/children/coparent-requests`, { firstName, lastName }).then(r => r.data),
    onSuccess: (data, vars) => {
      setLinkPrompt(null);
      setAddingChild(false);
      createChild.reset();
      setChildInitial(null);
      if (vars?.pending) consumePending(vars.pending.firstName, vars.pending.lastName);
      setCoparentMsg(data?.message || 'If that child is registered here, their parent has been asked.');
    },
  });

  const openLinkPrompt = (names, pending) => {
    requestCoparent.reset();
    setLinkPrompt({ firstName: names?.firstName || '', lastName: names?.lastName || '', pending: pending || null });
  };

  const editChild = useMutation({
    mutationFn: ({ id, ...payload }) =>
      axios.patch(`/clubs/${clubSlug}/children/${id}`, payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-children', clubSlug] });
      setEditingChildId(null);
    },
  });

  const deleteChild = useMutation({
    mutationFn: id => axios.delete(`/clubs/${clubSlug}/children/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-children', clubSlug] }),
  });

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (!membership) return <Navigate to={`/${clubSlug}`} replace />;

  const roleLabel = (membership.role || '').replace(/_/g, ' ');
  const seasonEndYear = currentSeasonEndYear();

  return (
    <div className="account-page">
      <Navbar club={club} />
      {/* Parent tour hops here for its "Your family" step (never auto-starts). */}
      <GuidedTour club={club} surface="account" />

      <PageHeader
        icon={User}
        title="My Account"
        subtitle={<>Manage your profile and family at {club?.name || 'this club'}.</>}
      />

      <div className="account-container">
        {/* ── Profile ────────────────────────────────────────── */}
        <section className="account-section">
          <h2 className="account-section-title"><User size={18} /> Profile</h2>
          <div className="account-card">
            <div className="profile-row">
              <span className="profile-label">Name</span>
              <span className="profile-value">{user.firstName} {user.lastName}</span>
            </div>
            <div className="profile-row">
              <span className="profile-label">Email</span>
              <span className="profile-value">{user.email || <em className="muted">Not set</em>}</span>
            </div>
            <div className="profile-row">
              <span className="profile-label">Role at this club</span>
              <span className="profile-value" style={{ textTransform: 'capitalize' }}>{roleLabel}</span>
            </div>
            {membership.teamName && (
              <div className="profile-row">
                <span className="profile-label">Team</span>
                <span className="profile-value">{membership.teamName}</span>
              </div>
            )}
          </div>
          {/* WT-19: this used to promise profile editing "in a later update"
              and send everyone, admins included, to a club admin. Nobody can
              change a name or a confirmed email in the app, so say who can. */}
          <p className="section-help" style={{ marginTop: 8 }}>
            To change your name or email, contact{' '}
            <a href={`mailto:${ACCOUNT_HELP_EMAIL}`} style={{ color: 'var(--green)', textDecoration: 'underline' }}>{ACCOUNT_HELP_EMAIL}</a>.{' '}
            {membership.role === 'admin'
              ? 'You can change roles and teams in Club Admin → Members.'
              : 'Your club\'s admins look after your role and team.'}
          </p>
        </section>

        {/* ── Notifications ──────────────────────────────────── */}
        <section className="account-section">
          <h2 className="account-section-title"><Bell size={18} /> Notifications</h2>
          <NotificationsToggle />
        </section>

        {/* ── My Family ──────────────────────────────────────── */}
        {!isSponsorLogin && (
        <section className="account-section" data-tour="account-family">
          <div className="section-head-row">
            <h2 className="account-section-title"><Users size={18} /> My family</h2>
            {!addingChild && (
              <button
                className="btn btn-primary btn-sm"
                onClick={() => { setChildInitial(null); setAddingChild(true); setEditingChildId(null); setInvitingChildId(null); }}
              >
                <UserPlus size={14} /> Add child
              </button>
            )}
          </div>

          <>
              <p className="section-help">
                Add your own children — the kids you're the parent or guardian for, not the whole
                team. Each child you add appears on their team's roster, and you'll be able to
                respond Going / Not going for them on every event card. Invite their other parent
                by email so you both stay in the loop.
              </p>

              {coparentMsg && (
                <div className="account-card" style={{ display: 'flex', alignItems: 'center', gap: 10, borderLeft: '3px solid #2D7A4F' }}>
                  <Check size={16} style={{ color: '#2D7A4F', flexShrink: 0 }} />
                  <p style={{ margin: 0, fontSize: 13.5 }}>
                    {coparentMsg} You'll get a notification once they confirm. Nothing after a few
                    days? Ask your child's other parent to invite you from My account (Invite co-parent).
                  </p>
                  <button onClick={() => setCoparentMsg('')} aria-label="Dismiss"
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', lineHeight: 0 }}>
                    <X size={14} />
                  </button>
                </div>
              )}

              {addedMsg && (
                <div className="account-card" style={{ display: 'flex', alignItems: 'center', gap: 10, borderLeft: '3px solid #2D7A4F' }}>
                  <Check size={16} style={{ color: '#2D7A4F', flexShrink: 0 }} />
                  <p style={{ margin: 0, fontSize: 13.5 }}>{addedMsg}</p>
                  <button onClick={() => setAddedMsg('')} aria-label="Dismiss"
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', lineHeight: 0 }}>
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Children the club listed on this parent's invite, still to add */}
              {pendingChildren.length > 0 && !addingChild && (
                <div className="account-card" style={{ borderLeft: '3px solid #C4943A' }}>
                  <p style={{ margin: '0 0 10px', fontSize: 13.5 }}>
                    <strong>Your club is expecting {pendingChildren.length === 1 ? 'a child' : `${pendingChildren.length} children`} from you</strong> —
                    add {pendingChildren.length === 1 ? 'them' : 'each one'} below (you'll just need their date of birth).
                    If their other parent has already added them, tap <strong>Already added?</strong> to
                    ask to be linked instead.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {pendingChildren.map((c, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        {/* W-15: fixed name column so the Add now / Dismiss
                            buttons line up down the list. */}
                        <span style={{ fontSize: 14, fontWeight: 600, flex: '0 0 150px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.firstName} {c.lastName || ''}
                        </span>
                        <button className="btn btn-primary btn-sm"
                          onClick={() => { setChildInitial({ firstName: c.firstName, lastName: c.lastName || '' }); setAddingChild(true); setEditingChildId(null); setInvitingChildId(null); }}>
                          <UserPlus size={13} /> Add now
                        </button>
                        <button className="btn btn-ghost btn-sm"
                          onClick={() => openLinkPrompt({ firstName: c.firstName, lastName: c.lastName || '' }, { firstName: c.firstName, lastName: c.lastName || '' })}>
                          Already added?
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => consumePending(c.firstName, c.lastName || '')}>
                          Dismiss
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {addingChild && (
                <div className="account-card">
                  <ChildForm
                    key={childInitial ? `${childInitial.firstName}-${childInitial.lastName}` : 'blank'}
                    initial={childInitial || undefined}
                    teams={teams}
                    seasonEndYear={seasonEndYear}
                    onCancel={() => { setAddingChild(false); setChildInitial(null); }}
                    onSubmit={payload => createChild.mutate(payload)}
                    submitting={createChild.isPending}
                    error={createChild.error}
                    submitLabel="Add child"
                    onAskLink={names => openLinkPrompt(names, childInitial ? { firstName: childInitial.firstName, lastName: childInitial.lastName || '' } : null)}
                  />
                </div>
              )}

              {/* B-32 / B-76: ask to be linked to a child the other parent
                  already added. The reply is the same whether or not that
                  child is registered here — it never tells you (B-76), and
                  never reveals anything about another family. */}
              {linkPrompt && (
                <div
                  onClick={() => setLinkPrompt(null)}
                  style={{ position: 'fixed', inset: 0, background: 'rgba(44,43,40,0.55)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
                >
                  <div onClick={e => e.stopPropagation()} className="account-card" role="dialog" aria-modal="true" aria-labelledby="link-child-title"
                    style={{ maxWidth: 440, width: '100%', background: '#fff' }}>
                    <h3 id="link-child-title" style={{ margin: '0 0 8px', fontSize: 16 }}>Ask to be linked to your child</h3>
                    <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
                      If your child's other parent has already added them here, we'll ask that parent
                      to confirm. Once they do, your child appears in your family — no need to add
                      them twice. To protect children's privacy we won't say whether we found them.
                    </p>
                    <form onSubmit={e => {
                      e.preventDefault();
                      const firstName = linkPrompt.firstName.trim();
                      const lastName = linkPrompt.lastName.trim();
                      if (!firstName || !lastName) return;
                      requestCoparent.mutate({ firstName, lastName, pending: linkPrompt.pending });
                    }}>
                      <label className="form-field" style={{ marginBottom: 10 }}>
                        <span>Child's first name</span>
                        <input className="input" autoFocus={!linkPrompt.firstName} value={linkPrompt.firstName} maxLength={100}
                          onChange={e => setLinkPrompt(p => ({ ...p, firstName: e.target.value }))} required />
                      </label>
                      <label className="form-field" style={{ marginBottom: 12 }}>
                        <span>Child's last name</span>
                        <input className="input" value={linkPrompt.lastName} maxLength={100}
                          onChange={e => setLinkPrompt(p => ({ ...p, lastName: e.target.value }))} required />
                      </label>
                      <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
                        Use the name exactly as the other parent will have entered it.
                      </p>
                      {requestCoparent.isError && (
                        <div className="form-error" style={{ marginBottom: 10 }}>
                          <AlertCircle size={14} /> {errorText(requestCoparent.error, 'Could not send the request. Try again.')}
                        </div>
                      )}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <button type="submit" className="btn btn-primary"
                          disabled={requestCoparent.isPending || !linkPrompt.firstName.trim() || !linkPrompt.lastName.trim()}>
                          {requestCoparent.isPending ? 'Sending…' : 'Send co-parenting request'}
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setLinkPrompt(null)}>Cancel</button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {childrenLoading && <div className="account-loading">Loading family…</div>}

              {!childrenLoading && children.length === 0 && !addingChild && (
                <div className="account-empty">
                  <div className="empty-icon"><Users size={28} /></div>
                  <p>No children added yet. Tap <strong>Add child</strong> to set one up.</p>
                </div>
              )}

              <div className="children-list">
                {children.map(child => (
                  <ChildCard
                    key={child.id}
                    child={child}
                    teams={teams}
                    seasonEndYear={seasonEndYear}
                    editing={editingChildId === child.id}
                    inviting={invitingChildId === child.id}
                    onStartEdit={() => { setEditingChildId(child.id); setInvitingChildId(null); setAddingChild(false); }}
                    onCancelEdit={() => setEditingChildId(null)}
                    onSubmitEdit={payload => editChild.mutate({ id: child.id, ...payload })}
                    editPending={editChild.isPending && editChild.variables?.id === child.id}
                    editError={editChild.error}
                    onStartInvite={() => { setInvitingChildId(child.id); setEditingChildId(null); setAddingChild(false); }}
                    onCancelInvite={() => setInvitingChildId(null)}
                    onDelete={() => {
                      const ok = window.confirm(
                        `Remove ${child.firstName} ${child.lastName} from your family?\n\n` +
                        `Their RSVPs and history are preserved. You can re-add them later if needed.`
                      );
                      if (ok) deleteChild.mutate(child.id);
                    }}
                    clubSlug={clubSlug}
                  />
                ))}
              </div>
          </>
        </section>
        )}

        {/* ── Privacy & data (GDPR Week 6) ───────────────────── */}
        <section className="account-section">
          <h2 className="account-section-title"><Lock size={18} /> Privacy & data</h2>
          <PrivacySettings />
        </section>

        {/* ── Password (B-65 addition) ────────────────────────── */}
        <section className="account-section">
          <h2 className="account-section-title"><KeyRound size={18} /> Password</h2>
          <PasswordSection />
        </section>

        {/* ── Sign out ───────────────────────────────────────── */}
        <section className="account-section">
          <h2 className="account-section-title"><LogOut size={18} /> Sign out</h2>
          <div className="account-card">
            <p className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
              Sign out of this Marakin session on this device.
            </p>
            <button className="btn btn-ghost btn-sm" onClick={() => { logout(); navigate('/'); }}>
              <LogOut size={14} /> Log out
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

// ─── ChildForm — used for both create and edit ───────────────────
function ChildForm({ teams, seasonEndYear, onSubmit, onCancel, submitting, error, submitLabel, initial, onAskLink }) {
  const [firstName, setFirstName] = useState(initial?.firstName || '');
  const [lastName, setLastName]   = useState(initial?.lastName  || '');
  const [dateOfBirth, setDateOfBirth] = useState(formatDob(initial?.dateOfBirth) || '');
  const [teamIds, setTeamIds] = useState(initial?.teams?.map(t => t.id) || []);
  const today = new Date().toISOString().slice(0, 10);

  // Currently-assigned teams (for edit mode) — used to detect coach overrides
  const initiallyAssigned = new Set(initial?.teams?.map(t => t.id) || []);

  const childSeasonAge = seasonAgeOnAug31(dateOfBirth, seasonEndYear);

  const submit = e => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !dateOfBirth) return;
    onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dateOfBirth,
      teamIds,
    });
  };

  return (
    <form className="child-form" onSubmit={submit}>
      <div className="form-row two">
        <label className="form-field">
          <span>First name</span>
          <input className="input" value={firstName} onChange={e => setFirstName(e.target.value)} required autoFocus={!initial} />
        </label>
        <label className="form-field">
          <span>Last name</span>
          <input className="input" value={lastName} onChange={e => setLastName(e.target.value)} required />
        </label>
      </div>

      <label className="form-field">
        <span>Date of birth</span>
        <input
          className="input"
          type="date"
          max={today}
          value={dateOfBirth}
          onChange={e => setDateOfBirth(e.target.value)}
          required
        />
        <small className="form-hint">
          Used to apply age-appropriate privacy settings and to filter the team list to age-eligible
          groups. Under-18s are never included in aggregated audience data.
        </small>
      </label>

      <fieldset className="form-field form-fieldset">
        <legend>Teams</legend>
        {teams.length === 0 ? (
          <small className="muted">No teams in this club yet. You can assign teams later.</small>
        ) : (
          <>
            {dateOfBirth && childSeasonAge != null && (
              <p className="form-hint" style={{ marginBottom: 6 }}>
                Eligible age group for the {seasonEndYear - 1}/{String(seasonEndYear).slice(-2)} season:{' '}
                <strong>U{childSeasonAge}s</strong>
              </p>
            )}
            <div className="team-chip-grid">
              {teams.map(t => {
                const band = teamAgeBand(t.name);
                const eligibleByAge = dateOfBirth && band && childSeasonAge != null && band.age === childSeasonAge;
                const adultOrUnknown = !band;
                const active = teamIds.includes(t.id);
                const wasInitial = initiallyAssigned.has(t.id);
                const isOverride = active && wasInitial && !eligibleByAge;
                const dobNotSet = !dateOfBirth;

                // States:
                //   eligible (clickable, normal)
                //   override (was set by coach, locked on)
                //   locked (not eligible, not currently assigned, not clickable)
                if (eligibleByAge) {
                  return (
                    <button
                      type="button"
                      key={t.id}
                      className={`team-chip ${active ? 'active' : ''}`}
                      onClick={() => setTeamIds(prev => active ? prev.filter(id => id !== t.id) : [...prev, t.id])}
                    >
                      {active && <Check size={12} />} {t.name}
                    </button>
                  );
                }
                if (isOverride) {
                  return (
                    <span
                      key={t.id}
                      className="team-chip override active"
                      title="Set by coach — only they can remove it."
                    >
                      <ShieldCheck size={12} /> {t.name}
                      <span className="team-chip-sub">coach</span>
                    </span>
                  );
                }
                // Locked — not eligible AND not currently a coach override
                const lockedTitle = dobNotSet
                  ? 'Enter date of birth to see eligible teams'
                  : adultOrUnknown
                    ? "Adult or non-age-graded team — ask a coach to add this child"
                    : `Age group ${band.label} — outside this child's eligible band. Ask a coach to add them.`;
                return (
                  <span key={t.id} className="team-chip locked" title={lockedTitle}>
                    <Lock size={11} /> {t.name}
                  </span>
                );
              })}
            </div>
            <small className="form-hint">
              Only age-eligible teams are tickable. Need {firstName || 'this child'} in another team?
              Ask a coach or team manager — they can override the age rule via the club admin panel.
            </small>
          </>
        )}
      </fieldset>

      {error && (
        <div className="form-error">
          <AlertCircle size={14} /> {errorText(error, 'Something went wrong. Try again.')}
        </div>
      )}

      <div className="form-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
          {submitting ? 'Saving…' : submitLabel}
        </button>
      </div>

      {/* B-76: add-only — the co-parent request, prefilled with what's typed. */}
      {onAskLink && (
        <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
          Has your child's other parent already added them on Marakin?{' '}
          <button type="button" className="btn btn-ghost btn-sm"
            onClick={() => onAskLink({ firstName: firstName.trim(), lastName: lastName.trim() })}>
            Ask to be linked instead
          </button>
        </p>
      )}
    </form>
  );
}

// ─── ChildCard ───────────────────────────────────────────────────
function ChildCard({
  child, teams, seasonEndYear, editing, inviting,
  onStartEdit, onCancelEdit, onSubmitEdit, editPending, editError,
  onStartInvite, onCancelInvite,
  onDelete, clubSlug,
}) {
  const age = ageFromDob(child.dateOfBirth);
  const coparents = (child.parents || []).filter(p => !p.isMe);

  return (
    <div className="child-card">
      <div className="child-card-head">
        <div className="child-avatar">{initialsOf(child.firstName, child.lastName)}</div>
        <div className="child-info">
          <div className="child-name">{child.firstName} {child.lastName}</div>
          <div className="child-meta">
            {age != null && <span>{age}y</span>}
            {child.teams.length > 0 && (
              <>
                {age != null && <span className="dot">·</span>}
                <span>
                  {child.teams.map((t, i) => (
                    <React.Fragment key={t.id}>
                      {i > 0 && ', '}
                      {t.name}
                      {/* B-90: a place this family asked for, not yet confirmed */}
                      {t.pending && <span className="muted"> (waiting for the team manager to confirm)</span>}
                    </React.Fragment>
                  ))}
                </span>
              </>
            )}
            {child.teams.length === 0 && (
              <>
                {age != null && <span className="dot">·</span>}
                <span className="muted">No teams</span>
              </>
            )}
          </div>
          {/* B-90 (QA S7): a place the team manager / an admin declined, shown for a week */}
          {(child.declinedTeams || []).length > 0 && (
            <div className="child-coparents">
              The team manager didn't confirm {child.declinedTeams.map(t => t.name).join(', ')}.
            </div>
          )}
          {coparents.length > 0 && (
            <div className="child-coparents">
              Also managed by {coparents.map(p =>
                `${p.firstName}${p.lastName ? ' ' + p.lastName : ''}`).join(', ')}
            </div>
          )}
        </div>
      </div>

      <div className="child-actions">
        <button className="btn btn-ghost btn-sm" onClick={onStartEdit}>
          <Edit2 size={13} /> Edit
        </button>
        <button className="btn btn-ghost btn-sm" onClick={onStartInvite}>
          <Mail size={13} /> Invite co-parent
        </button>
        <button className="btn btn-ghost btn-sm btn-danger-ghost" onClick={onDelete}>
          <Trash2 size={13} /> Remove
        </button>
      </div>

      {editing && (
        <div className="child-expanded">
          <ChildForm
            teams={teams}
            seasonEndYear={seasonEndYear}
            initial={child}
            onCancel={onCancelEdit}
            onSubmit={onSubmitEdit}
            submitting={editPending}
            error={editError}
            submitLabel="Save changes"
          />
        </div>
      )}

      {inviting && (
        <div className="child-expanded">
          <CoparentInviteForm
            clubSlug={clubSlug}
            child={child}
            onCancel={onCancelInvite}
          />
        </div>
      )}
    </div>
  );
}

// ─── CoparentInviteForm ──────────────────────────────────────────
function CoparentInviteForm({ clubSlug, child, onCancel }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const submit = async e => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `/clubs/${clubSlug}/children/${child.id}/invite-coparent`,
        { email: email.trim() }
      );
      setSent(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send invite');
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!sent?.inviteUrl) return;
    try {
      await navigator.clipboard.writeText(sent.inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be blocked — link is selectable anyway */
    }
  };

  if (sent) {
    return (
      <div className="invite-success">
        <p>
          Invite generated for <strong>{sent.email}</strong>. We've emailed it — if it doesn't
          arrive in a few minutes, share the link below directly.
        </p>
        <div className="invite-link-row">
          <code className="invite-link" onClick={e => {
            const range = document.createRange();
            range.selectNodeContents(e.currentTarget);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
          }}>
            {sent.inviteUrl}
          </code>
          <button type="button" className="btn btn-sm" onClick={copyLink}>
            <Copy size={13} /> {copied ? 'Copied' : 'Copy'}
          </button>
          <WhatsAppButton
            text={`Hi! I've added ${child.firstName} on Marakin. Join as their other parent/carer to see fixtures and reply for them 👇\n${sent.inviteUrl}`}
            clubSlug={clubSlug} className="btn btn-sm" size={13}
          />
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <form className="child-form" onSubmit={submit}>
      <label className="form-field">
        <span>Co-parent email</span>
        <input
          className="input"
          type="email"
          placeholder="partner@example.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          autoFocus
        />
        <small className="form-hint">
          They'll get a link to also RSVP and manage {child.firstName}'s account.
        </small>
      </label>
      {error && (
        <div className="form-error"><AlertCircle size={14} /> {error}</div>
      )}
      <div className="form-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send invite'}
        </button>
      </div>
    </form>
  );
}
